<ng-container *transloco="let t">
  <div class="relative">
    <!-- Trigger: avatar + name + role, opens the popup. Bordered card that
         highlights while open, matching the DS shell. -->
    <button
      type="button"
      (click)="toggle()"
      class="flex w-full cursor-pointer items-center gap-2.5 rounded-card p-2.5 text-left transition-colors hover:bg-sunken"
      [class.bg-sunken]="open()"
      aria-haspopup="menu"
      [attr.aria-expanded]="open()"
    >
      <span
        class="flex size-[34px] shrink-0 items-center justify-center rounded-full bg-brand font-body text-caption font-bold text-white"
      >
        {{ userInitials() }}
      </span>
      <span class="flex min-w-0 grow flex-col gap-0.5">
        <span class="truncate font-body text-[15px] font-semibold leading-5 text-ink-1">
          {{ displayName() }}
        </span>
        <span class="truncate text-caption font-semibold text-ink-3">
          {{ role() }}
        </span>
      </span>
      <app-icon
        class="shrink-0 text-ink-3"
        [name]="open() ? 'icon-arrow-collapse' : 'icon-arrow-dropdown'"
        [size]="20"
      />
    </button>

    @if (open()) {
      <!-- Drop-up popup: sits above the trigger since the card is at the bottom
           of the sidebar. Themes for free via semantic tokens. -->
      <div
        role="menu"
        class="absolute bottom-[calc(100%+10px)] left-0 z-[60] w-full min-w-[280px] max-w-[calc(100vw-2rem)] rounded-card bg-card shadow-overlay"
      >
        <!-- Identity -->
        <div class="flex items-center gap-3 p-4 pb-3.5">
          <span
            class="flex size-11 shrink-0 items-center justify-center rounded-full bg-brand font-body text-h3 font-bold text-white"
          >
            {{ userInitials() }}
          </span>
          <div class="flex min-w-0 flex-col gap-0.5">
            <span class="truncate font-body text-base font-semibold leading-5 text-ink-1">
              {{ displayName() }}
            </span>
            @if (showEmail()) {
              <span class="truncate font-body text-[13px] leading-[17px] text-ink-3">
                {{ email() }}
              </span>
            }
            <app-label-badge class="mt-1 self-start">{{ role() }}</app-label-badge>
          </div>
        </div>

        <div class="h-px bg-hairline"></div>

        <!-- Personal preferences (theme, language, units live there now) -->
        @if (canOpenPreferences()) {
        <div class="px-4 py-2">
          <button
            type="button"
            (click)="goToPreferences()"
            class="flex min-h-10 w-full cursor-pointer items-center gap-2.5 rounded-control px-2 text-left font-body text-[15px] leading-5 text-ink-1 transition-colors hover:bg-sunken"
          >
            <app-icon name="icon-menu-settings1" [size]="20" class="text-ink-2" />
            {{ t('navbar.userCard.settings') }}
          </button>
        </div>
        }

        @if (canSwitchPortal()) {
          <div class="h-px bg-hairline"></div>

          <!-- Portal switch (super-admin only): a segmented Admin/Tenant control.
               Admin is amber (the back-office accent), Tenant is brand blue.
               Selecting the current portal is a no-op; the other navigates. -->
          <div class="px-4 pb-2 pt-3">
            <div class="mb-2 font-body text-[11px] font-semibold uppercase tracking-wide text-ink-3">
              {{ t('navbar.userCard.portal') }}
            </div>
            <div class="flex gap-1 rounded-pill bg-sunken p-[3px]">
              <button
                type="button"
                (click)="goToPortal('admin')"
                [attr.aria-pressed]="portalSwitchInAdmin()"
                class="h-8 flex-1 cursor-pointer rounded-pill font-body text-button font-bold transition-colors"
                [class]="portalSwitchInAdmin() ? 'bg-admin-accent text-admin-accent-ink' : 'text-ink-2 hover:text-ink-1'"
              >
                {{ t('navbar.userCard.portalAdmin') }}
              </button>
              <button
                type="button"
                (click)="goToPortal('tenant')"
                [disabled]="!canEnterTenant()"
                [attr.aria-pressed]="!portalSwitchInAdmin()"
                class="h-8 flex-1 rounded-pill font-body text-button font-bold transition-colors disabled:cursor-not-allowed disabled:opacity-40"
                [class]="!portalSwitchInAdmin() ? 'bg-brand text-white' : 'cursor-pointer text-ink-2 hover:text-ink-1'"
              >
                {{ t('navbar.userCard.portalTenant') }}
              </button>
            </div>
          </div>
        }

        <div class="h-px bg-hairline"></div>

        <!-- Sign out -->
        <div class="p-4 pt-3">
          <button
            type="button"
            (click)="onSignOut()"
            class="flex w-full cursor-pointer items-center justify-center gap-2 rounded-control p-2.5 font-body text-button font-semibold text-ink-1 transition-colors hover:bg-sunken"
            style="box-shadow: inset 0 0 0 1px var(--border-strong)"
          >
            <app-icon name="icon-sso" [size]="20" />
            {{ t('navbar.signOut') }}
          </button>
        </div>
      </div>
    }
  </div>
</ng-container>
